import { useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "lib/auth";
import { SCHOOL } from "data";

export default function LoginPage() {
  const { isAuthed, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin";

  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Already signed in? Skip the login screen entirely.
  if (isAuthed) return <Navigate to={from} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(username, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || "Sign in failed.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full px-3.5 py-[11px] border-[1.5px] border-line rounded-[9px] font-sans text-sm outline-none focus:border-green";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[linear-gradient(135deg,#003d2b_0%,#005C40_60%,#00724f_100%)] font-sans relative overflow-hidden px-4">
      <svg
        className="absolute inset-0 w-full h-full opacity-[0.06]"
        viewBox="0 0 1200 800"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        {Array.from({ length: 10 }).map((_, r) =>
          Array.from({ length: 16 }).map((_, c) => (
            <circle key={`${r}-${c}`} cx={c * 80 + 40} cy={r * 80 + 40} r="24" fill="none" stroke="#fff" strokeWidth="1" />
          ))
        )}
      </svg>

      <div className="relative z-[2] bg-white rounded-[20px] px-10 py-11 w-full max-w-[400px] shadow-[0_24px_64px_rgba(0,0,0,0.3)]">
        <div className="text-center mb-[30px]">
          <div className="w-16 h-16 rounded-full bg-green mx-auto mb-4 flex items-center justify-center">
            <svg width="40" height="40" viewBox="0 0 36 36" aria-hidden="true">
              <circle cx="18" cy="18" r="16" fill="#fff" opacity="0.15" />
              <ellipse cx="18" cy="16" rx="7" ry="7" fill="#fff" opacity="0.9" />
              <path d="M9 28 Q18 21 27 28" stroke="#f7efc8" strokeWidth="2" fill="none" />
            </svg>
          </div>
          <div className="font-serif text-[22px] font-bold text-green-dark">Admin Portal</div>
          <div className="text-[13px] text-muted mt-1">
            {SCHOOL.short || `${SCHOOL.name} & ${SCHOOL.college}`}
          </div>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div>
            <label htmlFor="admin-username" className="text-[13px] font-semibold text-muted block mb-1.5">
              Username
            </label>
            <input
              id="admin-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="text-[13px] font-semibold text-muted block mb-1.5">
              Password
            </label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="Enter your password"
              className={inputCls}
            />
          </div>

          {error && (
            <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-3 py-2" role="alert">
              {error}
            </div>
          )}

          <div className="flex justify-between items-center text-[13px]">
            <label className="flex items-center gap-1.5 text-muted cursor-pointer">
              <input type="checkbox" defaultChecked /> Remember me
            </label>
            <button type="button" className="text-green font-semibold bg-transparent border-none cursor-pointer p-0">
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            disabled={busy}
            className="bg-green text-white border-none rounded-[9px] py-[13px] font-sans font-bold text-[15px] cursor-pointer mt-1 transition-opacity hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy ? "Signing in…" : "Sign In to Dashboard"}
          </button>
        </form>

        <div className="text-center mt-5 pt-[18px] border-t border-line">
          <Link to="/" className="text-muted text-[13px] no-underline">
            ← Back to Website
          </Link>
        </div>

        <div className="mt-3.5 text-[11px] text-[#9aa] text-center bg-[#f4f6f8] rounded-lg p-2">
          🔒 Authorized access only
        </div>
      </div>
    </div>
  );
}
